// plan-modusign-style-acceptance §Phase1 follow-up — canvas-based PDF viewer.
//
// 기존 iframe 방식의 문제:
//   1. 브라우저 native PDF viewer 의 toolbar / scrollbar / zoom 이
//      "디자이너 작업 영역" 처럼 안 보임 ("그냥 PDF 열람" 느낌).
//   2. native viewer 가 자체 zoom 을 적용해서 오버레이 좌표가 실제
//      PDF 콘텐츠와 어긋남.
//
// 해결: pdfjs-dist 로 각 페이지를 정확한 (pageWidth × pageHeight) 크기의
// canvas 에 렌더. 오버레이 좌표 시스템과 1:1 매치 → 필드 위치가 정확.
// toolbar / scrollbar / zoom 컨트롤 없음 → 디자이너 UI 처럼 보임.

import { useEffect, useRef, useState, type CSSProperties, type JSX } from 'react';

// Vite 가 worker 를 정적 URL 로 빌드해 주는 패턴.
// `?url` 쿼리는 Vite 의 asset import — 결과는 `/assets/pdf.worker-xyz.mjs` 같은 URL.
import * as pdfjsLib from 'pdfjs-dist';
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

// 한 번만 설정. HMR 로 재로딩되어도 idempotent.
if (typeof window !== 'undefined' && !pdfjsLib.GlobalWorkerOptions.workerSrc) {
  pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;
}

interface Props {
  /** Presigned GET URL or any fetchable PDF URL. */
  url: string;
  /** 각 페이지의 렌더 너비 (px) — 오버레이 좌표 시스템과 동일해야 함. */
  pageWidth: number;
  /** 각 페이지의 렌더 높이 (px) — 오버레이 좌표 시스템과 동일해야 함. */
  pageHeight: number;
  /**
   * 실제 PDF 파일에서 검출된 페이지 수를 알리는 콜백 (옵션).
   * 디자이너가 운영자가 입력한 page count 와 비교해 mismatch 시 경고 가능.
   */
  onPageCount?: (count: number) => void;
  /**
   * 이 캔버스가 화면에서 실제로 보여지는 배율. 디자이너는 바깥 컨테이너에
   * `transform: scale(zoom)` 을 걸므로 그 zoom 을 그대로 넘긴다.
   * 렌더 해상도를 화면 기기 픽셀과 1:1 로 맞추는 데 쓴다. 기본 1.
   */
  displayScale?: number;
  /**
   * 장 구성(2026-09-22) — i 번째 슬롯에 PDF 의 몇 번째 장(0-based)을 그릴지, `null`
   * 이면 빈 장. 없으면 PDF 장을 차례로 다 그린다. 디자이너가 장을 넣고 뺄 때 쓴다.
   */
  pageMap?: (number | null)[];
}

const containerSt: CSSProperties = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  flexDirection: 'column',
  // 페이지 사이는 간격 없이 붙임 — 오버레이 좌표 시스템이 (pageIndex * pageHeight) 기반
  gap: 0,
};

// PDF 페이지 '종이' 배경 — 항상 흰색(테마 무관). pdfjs 가 그리는 흰 페이지의 베이스라
// CSS 변수 대신 명명 상수로 둔다(규칙2 Canvas 예외). var(--bg-surface) 의 다크값을 쓰면
// 흰 PDF 가 어두운 종이로 보여 부적절.
const PDF_PAGE_BG = '#ffffff';

// ── 렌더 해상도 ────────────────────────────────────────────────────────────
//
// 캔버스는 **화면에 실제로 깔리는 기기 픽셀 수만큼** 그린다. 즉
//   렌더 배율 = 화면 표시 배율(displayScale = 디자이너 zoom) × devicePixelRatio
//
// 이 값보다 낮으면 확대되어 흐리고, 높으면 브라우저가 축소 리샘플링을 하면서
// 얇은 표 선·작은 글자에 에일리어싱(자글거림)이 생긴다. 1:1 이 유일한 지점이다.
//
// zoom 이나 dpr 이 바뀌면 다시 렌더한다.
//
// 하한 1 — 표시가 아무리 작아도 CSS 1px 미만으로는 안 줄인다.
const MIN_RENDER_SCALE = 1;

// 전체 페이지 합산 픽셀 상한(50M px ≈ 200MB RGBA). 양식은 최대 50페이지까지
// 가능해 고배율로 전 페이지를 그리면 메모리가 폭증한다.
const PIXEL_BUDGET = 50_000_000;

/**
 * 페이지당 렌더 배율 = displayScale × dpr, 픽셀 예산으로 상한, 1 로 하한.
 */
function computeRenderScale(
  pageW: number,
  pageH: number,
  numPages: number,
  dpr: number,
  displayScale: number,
): number {
  const target = Math.max(MIN_RENDER_SCALE, displayScale * dpr);
  const perPagePx = pageW * pageH;
  if (perPagePx <= 0 || numPages <= 0) return target;
  const budgetScale = Math.sqrt(PIXEL_BUDGET / (perPagePx * numPages));
  return Math.max(MIN_RENDER_SCALE, Math.min(target, budgetScale));
}

export function PdfCanvasViewer({
  url,
  pageWidth,
  pageHeight,
  onPageCount,
  displayScale = 1,
  pageMap,
}: Props): JSX.Element {
  // 배열 identity 가 아니라 내용으로 다시 그린다 — 부모가 매 렌더 새 배열을 줘도 된다.
  const pageMapKey = pageMap ? pageMap.join(',') : '';
  const [pageCount, setPageCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const canvasRefs = useRef<Array<HTMLCanvasElement | null>>([]);
  // 브라우저 확대/축소는 devicePixelRatio 를 바꾸고 resize 를 발화시킨다.
  const [dpr, setDpr] = useState(() =>
    typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1,
  );
  useEffect(() => {
    function onResize(): void {
      setDpr(window.devicePixelRatio || 1);
    }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  // R2 후속(워크플로 M18) — onPageCount 를 ref 로 안정화. 부모(SignTemplateDesignerPage)
  // 가 인라인 화살표로 넘겨 매 리렌더마다 신원이 바뀌는데, deps 에 두면 필드 드래그 등
  // 모든 상호작용에서 effect 가 재실행돼 PDF 를 통째로 다시 받았다(깜빡임/낭비).
  const onPageCountRef = useRef(onPageCount);
  onPageCountRef.current = onPageCount;

  // 렌더 재진입 방지(26-07-29) — operator-pwa 판과 같은 결함이 여기에도 있었다.
  // pdfjs 는 **같은 canvas 에 render() 가 겹치면** "Cannot use the same canvas during
  // multiple render() operations" 로 던진다. `cancelled` 플래그만 세우는 정리 함수는
  // 다음 '반복'을 막을 뿐 **이미 시작된 RenderTask 는 계속 돈다**.
  //
  // 두 겹으로 막는다:
  //   ① 정리 시 진행 중인 RenderTask 를 실제로 cancel()  ← 렌더 effect 의 tasks 배열
  //   ② 새 실행은 **직전 실행이 캔버스를 놓은 뒤에야** 렌더를 시작 ← 아래 pendingRef
  //      (cancel 은 즉시 반환하지만 캔버스가 풀리는 것은 그 promise 가 정리된 뒤라
  //       ①만으로는 경계가 남는다.)
  //
  // 합류(26-08-05) — ②가 **더 필요해졌다.** 아래 렌더 effect 는 이제 zoom(displayScale)·
  // dpr 변화에도 재실행되므로(해상도를 화면과 1:1 로 맞추려면 필수), 확대/축소를 조작할
  // 때마다 "cancel 직후 같은 canvas 에 새 render()" 경계에 닿는다. 종전엔 문서를 열 때만
  // 스치던 자리가 상시 경로가 된 셈이다.
  const pendingRef = useRef<Promise<void>>(Promise.resolve());

  // ── 1) 문서 로드 ────────────────────────────────────────────────────────
  // url 이 바뀔 때만. zoom/dpr 변화로 PDF 를 다시 받지 않도록 렌더와 분리한다.
  type PdfDocument = Awaited<ReturnType<typeof pdfjsLib.getDocument>['promise']>;
  const [pdfDoc, setPdfDoc] = useState<PdfDocument | null>(null);

  useEffect(() => {
    if (!url) return;
    let cancelled = false;
    setError(null);

    (async () => {
      try {
        const loadingTask = pdfjsLib.getDocument({ url });
        const pdf = await loadingTask.promise;
        if (cancelled) {
          void pdf.destroy();
          return;
        }
        setPdfDoc(pdf);
        setPageCount(pdf.numPages);
        onPageCountRef.current?.(pdf.numPages);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : '알 수 없는 오류');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [url]);

  // ── 2) 페이지 렌더 ──────────────────────────────────────────────────────
  // 해상도 입력(dpr / displayScale)이 바뀌면 문서는 그대로 두고 다시 그린다.
  useEffect(() => {
    const pdf = pdfDoc;
    if (!pdf) return;
    let cancelled = false;
    // 진행 중 렌더 태스크. cleanup 에서 반드시 cancel — 안 그러면 다음 실행이
    // 같은 canvas 에 render() 를 걸어 pdf.js 가
    // "Cannot use the same canvas during multiple render() operations" 로 던진다.
    const tasks: { cancel: () => void }[] = [];

    // ② 직렬화 — 직전 실행이 캔버스를 놓을 때까지 기다린 뒤에 그리기 시작한다.
    //    위 cleanup 의 cancel() 은 즉시 반환하지만 캔버스가 실제로 풀리는 것은 그
    //    RenderTask 의 promise 가 정리된 뒤다. 이 대기가 없으면 zoom 을 빠르게 조작할 때
    //    "Cannot use the same canvas during multiple render() operations" 가 간헐적으로 난다.
    const previous = pendingRef.current;
    let markDone!: () => void;
    pendingRef.current = new Promise<void>((r) => {
      markDone = r;
    });

    (async () => {
      try {
        await previous;
        if (cancelled) return;
        const slots = pageMap ?? Array.from({ length: pdf.numPages }, (_, i) => i);
        for (let slot = 0; slot < slots.length; slot += 1) {
          const src = slots[slot];
          const pageIdx = src === null || src === undefined ? -1 : src + 1;
          if (pageIdx < 1 || pageIdx > pdf.numPages) {
            // 빈 장 — 캔버스를 비운다(전에 그려진 장이 남지 않게).
            await new Promise<void>((r) => setTimeout(r, 0));
            const blank = canvasRefs.current[slot];
            if (blank) {
              blank.width = 1;
              blank.height = 1;
              blank.style.width = `${pageWidth}px`;
              blank.style.height = `${pageHeight}px`;
            }
            continue;
          }
          const page = await pdf.getPage(pageIdx);
          if (cancelled) return;

          // 페이지 원본 크기에 비례해서 pageWidth 에 맞춰 scale 계산.
          // (pageHeight 는 디자이너 컨테이너가 고정해 두는 슬롯 — 실제 PDF 비율과
          //  살짝 다를 수 있지만, A4 베이스 PDF 면 차이 미미.)
          const baseViewport = page.getViewport({ scale: 1 });
          const scale = pageWidth / baseViewport.width;
          const viewport = page.getViewport({ scale });

          // canvas 가 아직 mount 안 됐을 수 있으니 다음 tick 까지 대기.
          // requestAnimationFrame 은 탭이 hidden 이면 발화하지 않아 여기서 렌더가
          // 영구히 멈춘다(백그라운드 탭, 최소화 등). setTimeout 은 hidden 에서도 돈다.
          await new Promise<void>((r) => setTimeout(r, 0));
          if (cancelled) return;

          const canvas = canvasRefs.current[slot];
          if (!canvas) continue;

          const renderScale = computeRenderScale(
            viewport.width,
            viewport.height,
            pdf.numPages,
            dpr,
            displayScale,
          );
          canvas.width = Math.floor(viewport.width * renderScale);
          canvas.height = Math.floor(pageHeight * renderScale);
          canvas.style.width = `${pageWidth}px`;
          canvas.style.height = `${pageHeight}px`;

          const ctx = canvas.getContext('2d');
          if (!ctx) continue;
          // 재렌더 시 이전 변환/내용이 남지 않도록 초기화한 뒤 배율을 건다.
          ctx.setTransform(1, 0, 0, 1, 0, 0);
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          // 장 높이를 레이아웃 높이(pageHeight)에 정확히 맞춘다 — 폭 기준 배율만 쓰면 A4(595.28×841.89pt)는
          // 1122.9px 로 그려져 장 아래쪽 표 선이 입력 칸보다 ~1px 내려간다(2026-10-20). 서버 PageMap 과 같은 규칙.
          ctx.scale(renderScale, renderScale * (pageHeight / viewport.height));

          const task = page.render({ canvasContext: ctx, viewport });
          tasks.push(task);
          await task.promise;
        }
      } catch (err) {
        // cancel() 로 중단된 렌더는 정상 흐름이다 — 에러로 표시하지 않는다.
        const name = (err as { name?: string } | null)?.name;
        if (!cancelled && name !== 'RenderingCancelledException') {
          setError(err instanceof Error ? err.message : '알 수 없는 오류');
        }
      } finally {
        // ② 이 실행이 캔버스를 놓았음을 알린다. **반드시 finally** — 여기서 빠뜨리면
        //    다음 실행의 `await previous` 가 영구히 대기해 화면이 빈 채로 멈춘다.
        markDone();
      }
    })();

    return () => {
      cancelled = true;
      // ① 진행 중인 렌더를 실제로 취소. 플래그만 세우면 이미 시작된 RenderTask 는
      //    계속 돌아 다음 실행과 같은 canvas 를 두고 겹친다.
      for (const t of tasks) {
        try {
          t.cancel();
        } catch {
          // 이미 끝난 태스크의 cancel 은 무시.
        }
      }
    };
    // pageCount 는 canvas 개수를 결정하므로 유지 — 값이 바뀐 뒤 두 번째 패스가
    // 갓 mount 된 canvas ref 를 집는다. dpr / displayScale 은 렌더 해상도를
    // 결정하므로 필수 — 빠지면 zoom·확대 후 옛 해상도 비트맵이 늘어난 채 남는다.
  }, [pdfDoc, pageWidth, pageHeight, pageCount, dpr, displayScale, pageMapKey]);

  if (error) {
    return (
      <div
        style={{
          ...containerSt,
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--color-error)',
          fontSize: 13,
          background: 'var(--bg-danger-subtle)',
        }}
      >
        PDF를 불러오지 못했습니다. {error}
      </div>
    );
  }

  return (
    <div style={containerSt}>
      {Array.from({ length: (pageMap ? pageMap.length : pageCount) || 1 }, (_, i) => (
        <canvas
          key={i}
          ref={(el) => {
            canvasRefs.current[i] = el;
          }}
          style={{
            display: 'block',
            width: pageWidth,
            height: pageHeight,
            background: PDF_PAGE_BG,
          }}
        />
      ))}
    </div>
  );
}
